import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { CompositeFilterDescriptor, FilterDescriptor, isCompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SvgIcon } from '@progress/kendo-react-common';
import { GridExpandChangeEvent } from '@progress/kendo-react-grid';
import { homeIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { cloneDeep, pickBy } from 'lodash';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ChangeExamStatusModel, ExamGridModel, FileModel, PatientModel, TagModel } from 'models';
import { PatientAgeRangeModel } from 'models/PatientAgeRangeModel';

import { SubmitHandler } from 'core/forms';
import { useBoolean, useEvent, useMobileDetection, useSignalRQueryInvalidation, useValidatedParam } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Button, ButtonVariants, ColumnState, ComponentSizes, Page, PageHeader, TabButton, TabsContainer } from 'core/ui';
import { findOrThrow, hasText } from 'core/utils';

import { ActivityLogModal } from 'features/activity-log';
import { useApiClient } from 'features/api';
import { ApiWebSocketProvider } from 'features/api/fragments/ApiWebSocketProvider';
import { ExamGridDataStateChangeEvent } from 'features/exam';
import { ViewFileWindow } from 'features/file';
import { UploadWindow } from 'features/file/fragments';
import { useSessionLocation } from 'features/location';
import { useITransplant } from 'features/patient/hooks';
import { useUserSettings } from 'features/settings';
import { SettingsHelpers } from 'features/settings/services/settings-helpers';
import { WellKnownSettingName } from 'features/settings/types';
import { QRScanModal } from 'features/upload-exams/fragments/QRScanModal';

import { DEFAULT_FINAL_REPORT_VALUES, InitialValues } from '../constants';
import { FileViewerProvider } from '../contexts/FileViewerContext';
import { usePatientExamsQuery } from '../hooks';
import { PatientRoutes } from '../routes';
import { PatientEditService, PatientGridService } from '../services';
import { PatientFileModalFeature } from '../types';
import { PatientEditProps } from '../types/PatientEditProps';
import { PatientFormValues } from '../types/PatientFormValues';
import AISummary from './AI_Summary';
import { FileViewerIntegrated } from './FileViewerIntegrated';
import { ITransplantModal } from './ITransplantModal';
import { MergePatientModal } from './MergePatientModal';
import PatientDashboard from './PatientDashboard';
import { PatientEditExamGrid } from './PatientEditExamGrid';
import { PatientEditHeader } from './PatientEditHeader';
import { PatientEditModal } from './PatientEditModal';
import { PatientFilePreviewEdit } from './PatientFilePreviewEdit';
import { PatientFileViewModal } from './PatientFileViewModal';
import { PatientFiles } from './PatientFiles';
import { PatientFilesDashboard } from './PatientFilesDashboard';
import { ShareModal } from './ShareModal';
import { UploadFileModal } from './UploadFileModal';

const parseCaseTagIds = (ids: string | number[] | null | undefined): number[] | undefined => {
  if (Array.isArray(ids)) {
    // If it's already an array, filter to ensure all elements are valid numbers
    return ids.filter(Number.isFinite);
  }
  if (typeof ids === 'string') {
    const trimmedIds = ids.trim();
    // Check if it looks like a JSON array string
    if (trimmedIds.startsWith('[') && trimmedIds.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmedIds);
        if (Array.isArray(parsed)) {
          // If parsing succeeds and it's an array, filter for valid numbers
          const numericIds = parsed.filter(Number.isFinite);
          // Return the array only if it contains numbers
          return numericIds.length > 0 ? numericIds : undefined;
        }
      } catch (e) {
        // Log error if JSON parsing fails, but proceed to check if it's a single number string
        console.error('Failed to parse caseTagIds JSON string:', trimmedIds, e);
      }
    }
    // If not a valid JSON array string or parsing failed, try parsing as a single number
    const singleNum = Number(trimmedIds);
    // Return as a single-element array if it's a valid finite number
    return Number.isFinite(singleNum) ? [singleNum] : undefined;
  }
  // Return undefined if input is neither an array nor a string
  return undefined;
};

const MemoizedPatientEditHeader = React.memo(PatientEditHeader);

export const PatientEdit: FunctionComponent<PatientEditProps> = ({ isEditMode, patientId }) => {
  const { sessionLocation } = useSessionLocation(false);
  const id = useValidatedParam('id', 'integer', true);
  const apiClient = useApiClient();
  const { isMobile } = useMobileDetection();
  const { iTransplantData, isITransplantVisible, toggleITransplantDialog, handleITransplantStatusChange, handleITransplantAdd, handleITransplantUpdate } =
    useITransplant();
  const navigate = useNavigate();
  const location = useLocation();
  const fileViewMatch = useMatch(PatientRoutes.getFileView(':id', ':fileId'));
  const fileViewInWorklistMatch = useMatch(PatientRoutes.getFileViewInWorklist(':id', ':worklistSlug', ':fileId'));
  const { homePageUrl, reactPatientFormPage, legacyBaseUrl, reactUploadPage, userSettings } = useUserSettings(true);
  const [patient, setPatient] = useState<PatientModel | null>(null);
  const [donorForm, setDonorForm] = useState(InitialValues.donorForm);
  const [isFormDisabled, setIsDisabled] = useState(true);
  const [buttonText, setButtonText] = useState('Edit');
  const [dataState, setDataState] = useState(PatientGridService.getDefaultPatientExamDataState(id == null ? patientId || 0 : id));
  const [searchValue, setSearchValue] = useState('');
  const [activeExamToggle, setActiveExamToggle] = useState(true);
  const [shareToggle, setShareToggle] = useState(false);
  const [selectedExams, setSelectedExams] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [columnsState, setColumnsState] = useState<Record<string, ColumnState>>({});

  // Use TanStack Query for exam data with dynamic column optimization
  const currentPatientId = id || 0;
  const examsQuery = usePatientExamsQuery({
    patientId: currentPatientId,
    dataState,
    columnsState,
    enabled: !!currentPatientId,
    includeFiles: true, // Patient view always needs files for action button states
  });

  const exams = examsQuery.data?.data || [];
  const total = examsQuery.data?.total || 0;

  const [isMergeDonorVisible, setIsMergeDonorVisible] = useState(false);
  const [isDonorViewFilesVisible, setIsDonorViewFilesVisible] = useState(false);
  const [isDicomImagesVisible, setIsDicomImagesVisible] = useState(false);
  const [isDonorViewFinalReportVisible, setIsDonorViewFinalReportVisible] = useState(false);
  const [selectedExamStatus, setSelectedExamStatus] = useState('');
  const [finalReport, setFinalReport] = useState<FileModel>(DEFAULT_FINAL_REPORT_VALUES);
  const [selectedExamId, setSelectedExamId] = useState(0);
  const [gridData, setGridData] = useState(exams);
  const [isShareModalVisible, { setTrue: setShareModalVisible, setFalse: setShareModalHidden }] = useBoolean(false);
  const [isUploadFileModalVisible, { setTrue: setUploadFileModalVisible, setFalse: setUploadFileModalHidden }] = useBoolean(false);

  const [shareExamId, setShareExamId] = useState<number | null>(null);
  const [isActivityLogModalOpen, { setTrue: openActivityLogModal, setFalse: closeActivityLogModal }] = useBoolean(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [readRequestMode, setReadRequestMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive'>('Active');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTabView, setSelectedTabView] = useState<'Dashboard' | 'Exams' | 'Files' | 'AI Summary' | 'Cases'>('Exams');
  const [fileViewSourceTab, setFileViewSourceTab] = useState<'Dashboard' | 'Exams' | 'Files' | 'AI Summary' | 'Cases' | null>(null);

  const showDashboardTab = useMemo(
    () => SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowDashboardTab, false)?.value === 'true',
    [userSettings],
  );
  const showFilesTab = useMemo(
    () => SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowFilesTab, true)?.value === 'true',
    [userSettings],
  );
  const showAISummaryTab = useMemo(
    () => SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowAISummaryTab, false)?.value === 'true',
    [userSettings],
  );

  const caseTagIds = useMemo(() => SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.CaseTagIds, false)?.value, [userSettings]);
  const showCasesTab = showFilesTab;

  const isOpo = patient?.locationType === 'OPO';
  const patientVerbiage = isOpo ? 'Donor' : 'Patient';

  const [integratedViewerFile, setIntegratedViewerFile] = useState<FileModel | null>(null);
  const [integratedViewerContext, setIntegratedViewerContext] = useState<FileModel[]>([]);
  const [selectedFileForCasesPreview, setSelectedFileForCasesPreview] = useState<FileModel | null>(null);
  const [allTagsFlatState, setAllTagsFlatState] = useState<TagModel[]>([]);
  const [allHierarchicalTagsState, setAllHierarchicalTagsState] = useState<TagModel[]>([]);
  const [tagIdToTagMapState, setTagIdToTagMapState] = useState<Map<number, TagModel>>(new Map());
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  // Form submissions data from PatientFiles
  const [formData, setFormData] = useState<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formSubmissions: any[];
    areFormSubmissionsLoading: boolean;
    handleFormSubmissionsUpdated: () => Promise<void>;
  } | null>(null);

  // Track upload mode state for navigation warnings
  const [isUploadMode, setIsUploadMode] = useState(false);

  // Add SignalR integration for automatic patient exam refresh
  useSignalRQueryInvalidation({
    patientId: patient?.id,
    enableSmartInvalidation: true,
    enableDebugLogging: true,
    debounceMs: 300,

    // Invalidate patient-specific queries on relevant events
    invalidateOnExamChanged: ['patient-exams'],
    invalidateOnPatientChanged: ['patient-exams', 'patient-details'],
    invalidateOnFileChanged: ['patient-exams'], // Files can affect exam display
  });

  const handleViewFile = useCallback(
    (file: FileModel, context: FileModel[]) => {
      setIntegratedViewerFile(file);
      setIntegratedViewerContext(context);

      // Store which tab we're coming from so we can maintain context when viewing files
      setFileViewSourceTab(selectedTabView);

      // Navigate to the standard file view route
      const currentPatientId = patient?.id ?? id;
      const targetRoute = PatientRoutes.getFileView(currentPatientId, file.id);

      navigate(targetRoute, { replace: true });

      console.log('[PatientEdit] handleViewFile - showing file in integrated viewer:', file.fileName, 'from tab:', selectedTabView);
    },
    [navigate, patient?.id, id, selectedTabView],
  );

  // Callback to receive tag data from PatientFiles component
  const handleTagDataReady = useCallback(
    (tagData: { allFlatTags: TagModel[]; allHierarchicalTags: TagModel[]; tagIdToTagMap: Map<number, TagModel>; isLoading: boolean }) => {
      setAllTagsFlatState(tagData.allFlatTags);
      setAllHierarchicalTagsState(tagData.allHierarchicalTags);
      setTagIdToTagMapState(tagData.tagIdToTagMap);
      setIsLoadingTags(tagData.isLoading);
    },
    [],
  );

  // Callback to receive form submissions data from PatientFiles component
  const handleFormSubmissionsReady = useCallback(
    (formSubmissionsData: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formSubmissions: any[];
      areFormSubmissionsLoading: boolean;
      handleFormSubmissionsUpdated: () => Promise<void>;
    }) => {
      setFormData(formSubmissionsData);
    },
    [],
  );

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isFilesRoute = pathSegments.includes('files');
    const lastSegment = isFilesRoute ? 'Files' : pathSegments[pathSegments.length - 1];
    let initialTab: 'Dashboard' | 'Exams' | 'Files' | 'AI Summary' | 'Cases' = 'Exams';

    if (fileViewMatch || fileViewInWorklistMatch) {
      // If we're viewing a file and have a remembered source tab, use that
      if (fileViewSourceTab) {
        initialTab = fileViewSourceTab;
      } else {
        // Default to Files tab if no source tab is remembered
        initialTab = 'Files';
      }
    } else if (lastSegment === 'ai-summary') {
      initialTab = 'AI Summary';
    } else if (lastSegment === 'dashboard') {
      initialTab = 'Dashboard';
    } else if (lastSegment === 'cases') {
      initialTab = 'Cases';
    } else if (isFilesRoute) {
      initialTab = 'Files';
    }

    const visibleTabs = [showDashboardTab && 'Dashboard', 'Exams', showCasesTab && 'Cases', showFilesTab && 'Files', showAISummaryTab && 'AI Summary'].filter(
      Boolean,
    ) as string[];

    if (!visibleTabs.includes(initialTab)) {
      initialTab = visibleTabs.length > 0 ? (visibleTabs[0] as typeof initialTab) : 'Exams';
    }

    if (initialTab !== selectedTabView) {
      setSelectedTabView(initialTab);
    }

    if (integratedViewerFile && !fileViewMatch && !fileViewInWorklistMatch) {
      setIntegratedViewerFile(null);
      setIntegratedViewerContext([]);
      setFileViewSourceTab(null); // Clear the source tab when leaving file view
    }
  }, [
    location.pathname,
    fileViewMatch,
    fileViewInWorklistMatch,
    showDashboardTab,
    showCasesTab,
    showFilesTab,
    showAISummaryTab,
    selectedTabView,
    integratedViewerFile,
    fileViewSourceTab,
  ]);

  const handleChangeStatusSubmit = useEvent(async (data: ChangeExamStatusModel) => {
    await apiClient.exams.changeStatus(data);
    examsQuery.refetch();
  });

  const handleToggleActiveExams = useEvent((tab: 'Active' | 'Inactive') => {
    const clonedDataStateFilter = cloneDeep(dataState);

    if (clonedDataStateFilter.filter == null) {
      clonedDataStateFilter.filter = { logic: 'and', filters: [] };
    }
    if (!Array.isArray(clonedDataStateFilter.filter.filters)) {
      clonedDataStateFilter.filter.filters = [];
    }

    let activeFilter = clonedDataStateFilter.filter.filters.find((f): f is FilterDescriptor => !isCompositeFilterDescriptor(f) && f.field === 'active');

    const value = tab === 'Active';

    if (activeFilter == null) {
      activeFilter = { field: 'active', operator: 'eq', value };
      clonedDataStateFilter.filter.filters.push(activeFilter);
    } else {
      activeFilter.value = value;
    }

    setActiveExamToggle(value);
    setDataState(clonedDataStateFilter);
  });

  const handleActiveTabClick = useEvent(() => {
    const value = 'Active';
    setActiveTab(value);
    handleToggleActiveExams(value);
  });

  const handleInactiveTabClick = useEvent(() => {
    const value = 'Inactive';
    setActiveTab(value);
    handleToggleActiveExams(value);
  });

  const handleDataStateChange = useEvent((changeEvent: ExamGridDataStateChangeEvent) => {
    const dataState = changeEvent.dataState;
    if (!dataState?.filter) {
      if (dataState) dataState.filter = { logic: 'and', filters: [] };
      else return;
    }
    const filters = dataState.filter.filters;
    const checkPatientFilter = filters.find(
      (f: FilterDescriptor | CompositeFilterDescriptor): f is FilterDescriptor => !isCompositeFilterDescriptor(f) && f.field === 'patientId',
    );

    if (!checkPatientFilter) {
      filters.push({
        field: 'patientId',
        operator: 'eq',
        value: id,
      });
    }
    setDataState(dataState);
  });

  const handleShareChange = useEvent(async (dataItem: ExamGridModel) => {
    if (dataItem?.id) {
      try {
        await apiClient.exams.patchExam(dataItem.id, { Share: !dataItem.share });
        NotificationsService.displaySuccess('Exam updated successfully');
        examsQuery.refetch();
      } catch (error) {
        NotificationsService.displayError('Error updating exam');
        console.error('Share update error:', error);
      }
    }
  });

  const handleViewAttachments = useEvent((dataItem?: ExamGridModel) => {
    setSelectedExamId(dataItem?.id ?? 0);
    console.log('dataItem?.statusType', dataItem?.statusType);
    setSelectedExamStatus(dataItem?.statusType == null ? '' : dataItem?.statusType);
    setIsDonorViewFilesVisible(!isDonorViewFilesVisible);
  });

  const handleViewDicomImages = useEvent((dataItem: ExamGridModel) => {
    window.open(`/file-viewer/${dataItem.id}?feature=exams`, '_blank');
  });

  const handleViewFinalReport = useEvent((dataItem: ExamGridModel) => {
    if (dataItem.finalReportFile) setFinalReport(dataItem.finalReportFile);
    setIsDonorViewFinalReportVisible(!isDonorViewFinalReportVisible);
  });

  const handleExamUpdate = useEvent(() => {
    console.log('Exam updated');
    examsQuery.refetch();
  });

  const fetchExams = useCallback(() => {
    // TanStack Query automatically handles data fetching based on dataState changes
    // This function now serves as a manual refetch trigger for backward compatibility
    examsQuery.refetch();
  }, [examsQuery]);

  useEffect(() => {
    const fetchDonor = async () => {
      if (id) {
        try {
          const patientResponse = await apiClient.patientClient.getPatient(id);
          setPatient(patientResponse);
          setDonorForm(PatientEditService.copyModelToForm(patientResponse));
        } catch (error) {
          NotificationsService.displayError(`Failed to load ${patientVerbiage} data.`);
          console.error('Fetch patient error:', error);
        }
      }
    };

    fetchDonor();
  }, [apiClient, id, patientVerbiage]);

  useEffect(() => {
    setGridData(PatientGridService.initializePatientGrid(exams, isOpo, patient));

    const selectedIds = Object.keys(pickBy(selectedExams, (isSelected) => isSelected));
    const statusTypes = exams.filter((exam) => selectedIds.includes(exam?.id?.toString() ?? '')).map((exam) => exam?.statusType);
    setSelectedStatuses(statusTypes.filter((status): status is string => status !== null));
  }, [exams, isOpo, patient, selectedExams]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  const handleFormToggle = () => {
    setShowEditModal(!showEditModal);
  };
  const handleSubmit: SubmitHandler<PatientFormValues> = useEvent(async (data) => {
    if (patient == null) {
      throw new Error('Cannot save patient because the original record was not retrieved.');
    }

    setIsSubmitting(true);
    setIsDisabled(true);
    setButtonText('Saving...');

    const updatedPatientModel = PatientEditService.copyFormToModel(data, patient);
    try {
      await apiClient.patientClient.updatePatient(updatedPatientModel);
      const freshPatientData = await apiClient.patientClient.getPatient(updatedPatientModel.id);
      setPatient(freshPatientData);
      setDonorForm(PatientEditService.copyModelToForm(freshPatientData));
      NotificationsService.displaySuccess(`${patientVerbiage} updated successfully`);
      if (selectedExamId && readRequestMode) {
        navigate(`/exam/${selectedExamId}/read-request`);
      }
      setShowEditModal(false);
    } catch (error) {
      NotificationsService.displayError(`Error updating ${patientVerbiage.toLowerCase()}`);
      console.error('Update patient error:', error);
      setIsDisabled(false);
    } finally {
      setIsSubmitting(false);
      setButtonText('Edit');
    }
  });

  const toggleMergeDonorDialog = useCallback(() => {
    setIsMergeDonorVisible((prevIsMergeDonorVisible) => !prevIsMergeDonorVisible);
  }, [setIsMergeDonorVisible]);

  const handleShareClick = useEvent(() => {
    setShareModalVisible();
    setShareExamId(null);
  });

  const handleShareGridClick = useEvent((exam: ExamGridModel | null | undefined) => {
    setShareModalVisible();
    setShareExamId(exam?.id ?? null);
  });

  const handleUploadExamClick = useEvent(() => {
    if (reactUploadPage) {
      navigate(`/upload/patient/${id}`);
    } else {
      window.location.href = `${legacyBaseUrl}/DICOM/UploadDICOM?PatientId=${id}`;
    }
  });

  const handleUploadFileClick = useEvent(() => {
    setUploadFileModalVisible();
  });

  const handleUploadFileModalClose = useEvent(() => {
    setUploadFileModalHidden();
    examsQuery.refetch();
  });
  const breadcrumbs = useMemo(
    () => [
      {
        id: 'home',
        text: 'Home',
        icon: <StyledIcon icon={homeIcon} />,
        navigateToExternal: homePageUrl,
      },
      {
        id: 'patient',
        text: patientVerbiage,
        ...{
          navigateTo: reactPatientFormPage ? '/patient-2/' : undefined,
          navigateToExternal: reactPatientFormPage ? undefined : '/patient',
        },
      },
    ],
    [homePageUrl, patientVerbiage, reactPatientFormPage],
  );

  const handleBreadcrumbClick = useEvent((event: { id: string }) => {
    const selectedItem = findOrThrow(breadcrumbs, (item) => item.id === event.id, `Could not find selected item id: ${event.id}.`);

    if ('navigateTo' in selectedItem && hasText(selectedItem.navigateTo)) {
      navigate(selectedItem.navigateTo);
      return;
    } else if (hasText(selectedItem.navigateToExternal)) {
      window.location.href = selectedItem?.navigateToExternal;
    } else {
      throw new Error('Could not navigate because the selected breadcrumb item does not have a valid route.');
    }
  });

  const [ageRanges, setAgeRanges] = useState<PatientAgeRangeModel[] | undefined>();

  useEffect(() => {
    const fetchAgeRanges = async () => {
      try {
        const patientAgeRangeResponse = await apiClient.patientClient.getPatientAgeRange();
        setAgeRanges(patientAgeRangeResponse);
      } catch (error) {
        console.error('Fetch age ranges error:', error);
      }
    };

    fetchAgeRanges();
  }, [apiClient.patientClient]);

  const handleFormChange = useEvent((values: PatientFormValues) => {
    setDonorForm(values);
  });

  const handleReadRequest = useEvent((exam: ExamGridModel) => {
    // Check if we're in upload mode on Files tab and warn user
    if (selectedTabView === 'Files' && isUploadMode) {
      const shouldContinue = window.confirm('You have an upload in progress. Are you sure you want to navigate away? Any unsaved uploads will be lost.');
      if (!shouldContinue) {
        return;
      }
      // Exit upload mode if user confirms
      setIsUploadMode(false);
    }

    // Check UNOS validation for OPO locations
    if (isOpo && !PatientEditService.validateDonorForm(patient)) {
      console.log('[PatientEdit] UNOS validation failed, showing edit modal:', {
        patient: patient?.unosID,
        caseID: patient?.caseID,
        validationResult: PatientEditService.validateDonorForm(patient),
      });
      setSelectedExamId(exam.id ?? 0);
      setReadRequestMode(true);
      setShowEditModal(true);
    } else {
      navigate(`/exam/${exam.id}/read-request`);
    }
  });

  const closeEditWindow = () => {
    setReadRequestMode(false);
    setShowEditModal(false);
  };

  const [isLinkCreating, setIsLinkCreating] = useState(false);

  const [isQrModalVisible, setIsQrModalVisible] = useState<boolean>(false);
  const [linkId, setLinkId] = useState<string | null>();

  const handleQrModalClose = useEvent(() => {
    setIsQrModalVisible(false);
    setLinkId(null);
    setIsDonorViewFilesVisible(true);
  });

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    const newData = gridData?.map((item: ExamGridModel) => {
      if (item.id === event.dataItem.id) {
        item.expanded = !event.dataItem.expanded;
      }
      return item;
    });
    setGridData(newData);
  };

  const handleTabClick = useEvent((tabName: string, pathSuffix: string) => {
    if (selectedTabView !== tabName) {
      // Check if we're leaving Files tab while in upload mode
      if (selectedTabView === 'Files' && isUploadMode) {
        const shouldContinue = window.confirm('You have an upload in progress. Are you sure you want to navigate away? Any unsaved uploads will be lost.');
        if (!shouldContinue) {
          return;
        }
        // Exit upload mode if user confirms
        setIsUploadMode(false);
      }

      setSelectedTabView(tabName as 'Dashboard' | 'Exams' | 'Files' | 'AI Summary' | 'Cases');
      navigate(`/patient-2/edit/${id}${pathSuffix}`, { replace: true });
      setIntegratedViewerFile(null);
      setFileViewSourceTab(null); // Clear file view source when changing tabs
    }
  });

  const handleExamTabClick = useEvent(() => handleTabClick('Exams', ''));
  const handleFilesTabClick = useEvent(() => handleTabClick('Files', '/files'));
  const handleAISummaryTabClick = useEvent(() => handleTabClick('AI Summary', '/ai-summary'));
  const handleDashboardTabClick = useEvent(() => handleTabClick('Dashboard', '/dashboard'));
  const handleCasesTabClick = useEvent(() => handleTabClick('Cases', '/cases'));

  const handleFinalReportClosed = useEvent(() => {
    setIsDonorViewFinalReportVisible(false);
  });

  const handleCloseViewerIntegrated = useEvent(() => {
    const sourceTab = fileViewSourceTab;
    setIntegratedViewerFile(null);
    setIntegratedViewerContext([]);
    setFileViewSourceTab(null);

    // Navigate back to the appropriate tab based on where we came from
    const currentPatientId = patient?.id ?? id;
    let targetRoute = `/patient-2/edit/${currentPatientId}`;

    if (sourceTab === 'Files') {
      targetRoute += '/files';
    } else if (sourceTab === 'Cases') {
      targetRoute += '/cases';
    } else if (sourceTab === 'AI Summary') {
      targetRoute += '/ai-summary';
    } else if (sourceTab === 'Dashboard') {
      targetRoute += '/dashboard';
    }
    // For 'Exams' tab or null, we use the base route without suffix

    navigate(targetRoute, { replace: true });
  });

  const handleFileSelectionInViewer = useCallback(
    (file: FileModel) => {
      const currentFileId = fileViewMatch?.params?.fileId || fileViewInWorklistMatch?.params?.fileId;
      if (file.id.toString() !== currentFileId) {
        setIntegratedViewerFile(file);

        // Preserve worklist context if we're in one
        if (fileViewInWorklistMatch?.params?.worklistSlug) {
          const worklistParams = fileViewInWorklistMatch.params as { worklistSlug?: string; fileId?: string };
          const worklistSlug = worklistParams.worklistSlug;
          if (worklistSlug) {
            navigate(PatientRoutes.getFileViewInWorklist(patient?.id ?? id, worklistSlug, file.id), { replace: true });
            return;
          }
        }

        // Fallback to regular file view
        navigate(PatientRoutes.getFileView(patient?.id ?? id, file.id), { replace: true });
      }
    },
    [fileViewMatch?.params?.fileId, fileViewInWorklistMatch, navigate, patient?.id, id],
  );

  const handleDeleteFile = useCallback(
    async (fileId: number | string) => {
      const numericFileId = typeof fileId === 'string' ? parseInt(fileId, 10) : fileId;
      if (isNaN(numericFileId)) return;
      if (!window.confirm(`Are you sure you want to delete this file?`)) return;
      try {
        await apiClient.filesClient.deleteFile(numericFileId, 'msal-required');
        NotificationsService.displaySuccess('File deleted successfully.');
        handleCloseViewerIntegrated();
      } catch (error) {
        NotificationsService.displayError('Failed to delete file.');
        console.error('Delete file error:', error);
      }
    },
    [apiClient.filesClient, handleCloseViewerIntegrated],
  );

  const handleRenameFile = useCallback(
    async (file: FileModel, newName: string) => {
      if (!newName || newName === file.fileName) return;
      try {
        const patchDoc = [{ op: 'replace', path: '/fileName', value: newName }];
        await apiClient.filesClient.renameFile(file.id, patchDoc);
        NotificationsService.displaySuccess('File renamed successfully.');
        setIntegratedViewerFile((prev) => (prev ? { ...prev, fileName: newName } : null));
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === file.id ? { ...f, fileName: newName } : f)));
      } catch (error) {
        NotificationsService.displayError('Failed to rename file.');
        console.error('Rename file error:', error);
        throw error;
      }
    },
    [apiClient.filesClient],
  );

  const handleUpdateFileDescription = useCallback(
    async (file: FileModel, description: string) => {
      try {
        const patchDoc = [{ op: 'replace', path: '/description', value: description }];
        await apiClient.filesClient.renameFile(file.id, patchDoc);
        NotificationsService.displaySuccess('File description updated successfully.');
        setIntegratedViewerFile((prev) => (prev ? { ...prev, description: description } : null));
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === file.id ? { ...f, description: description } : f)));
      } catch (error) {
        NotificationsService.displayError('Failed to update file description.');
        console.error('Update file description error:', error);
        throw error;
      }
    },
    [apiClient.filesClient],
  );

  const handleUpdateFileTimestamps = useCallback(
    async (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => {
      const formatForApi = (date: Date | null | undefined): string | null => (date ? dayjs(date).toISOString() : null);
      const updatePayload: { captureTime?: string | null; eventTime?: string | null } = {};
      const stateUpdatePayload: Partial<Pick<FileModel, 'captureTime' | 'eventTime'>> = {};

      if (captureTime !== undefined) {
        const formattedTime = formatForApi(captureTime);
        updatePayload.captureTime = formattedTime;
        stateUpdatePayload.captureTime = formattedTime;
      }
      if (eventTime !== undefined) {
        const formattedTime = formatForApi(eventTime);
        updatePayload.eventTime = formattedTime;
        stateUpdatePayload.eventTime = formattedTime;
      }

      if (Object.keys(updatePayload).length === 0) return;

      try {
        const patchDoc: { op: string; path: string; value: string | null }[] = [];
        if ('captureTime' in updatePayload) {
          patchDoc.push({ op: 'replace', path: '/captureTime', value: updatePayload.captureTime ?? null });
        }
        if ('eventTime' in updatePayload) {
          patchDoc.push({ op: 'replace', path: '/eventTime', value: updatePayload.eventTime ?? null });
        }

        if (patchDoc.length === 0) return;

        await apiClient.filesClient.renameFile(file.id, patchDoc);
        NotificationsService.displaySuccess('Timestamps updated successfully.');
        const updatedFileInState: FileModel = {
          id: 0,
          fileName: '',
          fileType: '',
          fileSize: 0,
          created: '',
          ...integratedViewerFile,
          ...stateUpdatePayload,
        } as FileModel;

        setIntegratedViewerFile(updatedFileInState);
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === file.id ? { ...f, ...stateUpdatePayload } : f)));
      } catch (error) {
        NotificationsService.displayError('Failed to update timestamps.');
        console.error('Timestamp update error:', error);
        throw error;
      }
    },
    [apiClient.filesClient, integratedViewerFile],
  );

  const handleDownloadFile = useCallback(
    async (file: FileModel) => {
      if (!file?.location) {
        NotificationsService.displayError('File location is missing, cannot generate download link.');
        return;
      }
      try {
        const locationParts = file.location.split('/');
        if (locationParts.length < 2) {
          NotificationsService.displayError('Invalid file location format.');
          console.error('Invalid file location format:', file.location);
          return;
        }
        const containerName = locationParts[0];
        const blobName = locationParts.slice(1).join('/');

        const downloadSasUrl = await apiClient.filesClient.generateAzureBlobDownloadSasUrl(containerName, blobName);

        if (downloadSasUrl) {
          const link = document.createElement('a');
          link.href = downloadSasUrl;
          link.setAttribute('download', file.fileName || `download_${file.id}`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          NotificationsService.displayError('Could not get download URL.');
        }
      } catch (error) {
        NotificationsService.displayError('Failed to download file.');
        console.error('Download file error:', error);
      }
    },
    [apiClient.filesClient],
  );

  const handleUpdateFileTags = useCallback(
    async (fileId: number, tagIds: number[]) => {
      try {
        await apiClient.filesClient.updateFileTags(fileId, tagIds, 'msal-required');
        NotificationsService.displaySuccess('Tags updated successfully.');
        const updatedTags = tagIds.map((id) => tagIdToTagMapState.get(id)).filter((t): t is TagModel => !!t);
        setIntegratedViewerFile((prev) => (prev && prev.id === fileId ? { ...prev, tags: updatedTags } : prev));
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === fileId ? { ...f, tags: updatedTags } : f)));
      } catch (error) {
        NotificationsService.displayError('Failed to update tags.');
        console.error('Update tags error:', error);
        throw error;
      }
    },
    [apiClient.filesClient, tagIdToTagMapState],
  );

  const handleCasesFileSelect = useCallback((file: FileModel | null) => {
    setSelectedFileForCasesPreview(file);
  }, []);

  const visibleTabs = useMemo(
    () =>
      [showDashboardTab && 'Dashboard', 'Exams', showCasesTab && 'Cases', showFilesTab && 'Files', showAISummaryTab && 'AI Summary'].filter(
        Boolean,
      ) as string[],
    [showDashboardTab, showCasesTab, showFilesTab, showAISummaryTab],
  );

  return (
    <>
      {id && <ActivityLogModal show={isActivityLogModalOpen} entityType="patient" id={id} onClose={closeActivityLogModal} />}
      <Page>
        <PageHeader showSessionLocation title={`${patientVerbiage} Edit`} />
        <StyledPatientContainer>
          <StyledPatientEditHeader patientId={id} />

          {!integratedViewerFile && (
            <StyledTabsPanel>
              <StyledMainTabsContainer>
                {visibleTabs.map((tabName, index) => {
                  const isFirst = index === 0;
                  const isLast = index === visibleTabs.length - 1;
                  let corners: 'left' | 'right' | undefined = undefined;
                  if (isFirst && isLast) corners = 'left';
                  else if (isFirst) corners = 'left';
                  else if (isLast) corners = 'right';

                  let clickHandler: React.MouseEventHandler<HTMLButtonElement> | undefined;
                  if (tabName === 'Dashboard') clickHandler = handleDashboardTabClick;
                  else if (tabName === 'Exams') clickHandler = handleExamTabClick;
                  else if (tabName === 'Cases') clickHandler = handleCasesTabClick;
                  else if (tabName === 'Files') clickHandler = handleFilesTabClick;
                  else if (tabName === 'AI Summary') clickHandler = handleAISummaryTabClick;

                  return (
                    <TabButton key={tabName} type="button" selected={selectedTabView === tabName} onClick={clickHandler} roundedCorners={corners ?? 'left'}>
                      {tabName}
                    </TabButton>
                  );
                })}
              </StyledMainTabsContainer>

              {selectedTabView === 'Exams' && (
                <StyledToggleActiveExamsContainer>
                  <TabButton roundedCorners="left" type="button" selected={activeTab === 'Active'} onClick={handleActiveTabClick}>
                    Active
                  </TabButton>
                  <TabButton roundedCorners="right" type="button" selected={activeTab === 'Inactive'} onClick={handleInactiveTabClick}>
                    Inactive
                  </TabButton>
                </StyledToggleActiveExamsContainer>
              )}
            </StyledTabsPanel>
          )}

          <StyledTabsContentDiv>
            {integratedViewerFile ? (
              (() => {
                const worklistParams = fileViewInWorklistMatch?.params as { worklistSlug?: string } | undefined;
                const currentPath = worklistParams?.worklistSlug || '';

                return (
                  <FileViewerIntegrated
                    viewingFile={integratedViewerFile}
                    viewingFileContext={integratedViewerContext}
                    onCloseViewer={handleCloseViewerIntegrated}
                    onFileSelection={handleFileSelectionInViewer}
                    onFileDeleted={handleDeleteFile}
                    onFileRenamed={handleRenameFile}
                    onFileDescriptionUpdated={handleUpdateFileDescription}
                    onFileTimestampsUpdated={handleUpdateFileTimestamps}
                    onFileTagsUpdated={handleUpdateFileTags}
                    onFileDownloaded={handleDownloadFile}
                    allTagsFlat={allTagsFlatState}
                    allHierarchicalTags={allHierarchicalTagsState}
                    tagIdToTagMap={tagIdToTagMapState}
                    areTagsLoading={isLoadingTags}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formSubmissions={(formData?.formSubmissions ?? []) as any}
                    areFormSubmissionsLoading={formData?.areFormSubmissionsLoading ?? false}
                    onFormSubmissionsUpdated={formData?.handleFormSubmissionsUpdated ?? (() => Promise.resolve())}
                    patientId={Number(patient?.id || 0)}
                    currentPath={currentPath}
                    selectedWorklist={null}
                    allWorklists={null}
                    stackName={undefined}
                  />
                );
              })()
            ) : (
              <>
                {selectedTabView === 'Dashboard' && patient && <PatientDashboard patient={patient} patientId={Number(patient.id)} gridData={gridData} />}
                {selectedTabView === 'Exams' && (
                  <>
                    {isEditMode && (
                      <StyledActionsContainer>
                        <Button
                          size={ComponentSizes.LARGE}
                          type="button"
                          variant={ButtonVariants.SECONDARY}
                          iconClass="fa fa-share-nodes"
                          onClick={handleShareClick}
                        >
                          Share {patientVerbiage}
                        </Button>
                        <Button
                          size={ComponentSizes.LARGE}
                          type="button"
                          variant={ButtonVariants.SECONDARY}
                          iconClass="fa fa-upload"
                          onClick={handleUploadExamClick}
                        >
                          Upload Exam
                        </Button>
                        <Button
                          size={ComponentSizes.LARGE}
                          type="button"
                          variant={ButtonVariants.SECONDARY}
                          iconClass="fa fa-upload"
                          onClick={handleUploadFileClick}
                        >
                          Upload File
                        </Button>
                        {isOpo && (
                          <Button
                            size={ComponentSizes.LARGE}
                            type="button"
                            variant={ButtonVariants.SECONDARY}
                            onClick={toggleITransplantDialog}
                            iconClass="fa fa-file"
                          >
                            Upload iTransplant
                          </Button>
                        )}
                        <Button
                          size={ComponentSizes.LARGE}
                          type="button"
                          variant={ButtonVariants.SECONDARY}
                          onClick={toggleMergeDonorDialog}
                          iconClass="fa fa-object-ungroup"
                        >
                          Merge {patientVerbiage}
                        </Button>
                      </StyledActionsContainer>
                    )}
                    <StyledPatientExamGrid
                      featureView="Patient"
                      donorToolbar
                      columnsState={columnsState}
                      dataState={dataState}
                      gridData={gridData}
                      handleViewFinalReport={handleViewFinalReport}
                      handleViewAttachments={handleViewAttachments}
                      handleViewDicomImages={handleViewDicomImages}
                      handleChangeStatusSubmit={handleChangeStatusSubmit}
                      handleDataStateChange={handleDataStateChange}
                      activeExamToggle={activeExamToggle}
                      handleShareChange={handleShareChange}
                      setShareToggle={setShareToggle}
                      shareToggle={shareToggle}
                      searchValue={searchValue}
                      selectedExams={selectedExams}
                      selectedStatuses={selectedStatuses}
                      setColumnsState={setColumnsState}
                      setSelectedExams={setSelectedExams}
                      handleShareGridClick={handleShareGridClick}
                      handleReadClick={handleReadRequest}
                      handleExpandChange={handleExpandChange}
                      handleExamUpdate={handleExamUpdate}
                    />
                  </>
                )}
                {selectedTabView === 'Files' && patient && (
                  <ApiWebSocketProvider>
                    <PatientFiles
                      patientId={Number(patient.id)}
                      gridData={gridData}
                      onViewFile={handleViewFile}
                      onUploadModeChange={setIsUploadMode}
                      onTagDataReady={handleTagDataReady}
                      onFormSubmissionsReady={handleFormSubmissionsReady}
                    />
                  </ApiWebSocketProvider>
                )}
                {selectedTabView === 'AI Summary' && patient && <AISummary patientId={Number(patient.id)} />}
                {selectedTabView === 'Cases' && patient && (
                  <ApiWebSocketProvider>
                    <FileViewerProvider>
                      <div style={{ display: 'flex', height: '100%' }}>
                        <div style={{ flex: selectedFileForCasesPreview ? '1' : '1 1 100%', minWidth: 0 }}>
                          <PatientFilesDashboard
                            patientId={Number(patient.id)}
                            gridData={gridData}
                            onViewFile={handleViewFile}
                            targetParentTagIds={parseCaseTagIds(caseTagIds)}
                            showPreviewSidebar={false}
                            onFileSelect={handleCasesFileSelect}
                            allTagsFlat={allTagsFlatState}
                            tagIdToTagMap={tagIdToTagMapState}
                            areTagsLoading={isLoadingTags}
                          />
                        </div>
                        {selectedFileForCasesPreview && (
                          <div style={{ flex: '0 0 350px', width: '350px', borderLeft: '1px solid #ddd' }}>
                            <PatientFilePreviewEdit
                              selectedFile={selectedFileForCasesPreview}
                              onView={() => {
                                if (selectedFileForCasesPreview) {
                                  handleViewFile(selectedFileForCasesPreview, [selectedFileForCasesPreview]);
                                }
                              }}
                              onDownload={handleDownloadFile}
                              onDelete={() => handleDeleteFile(selectedFileForCasesPreview.id)}
                              onRename={handleRenameFile}
                              onUpdateDescription={handleUpdateFileDescription}
                              onUpdateTimestamps={handleUpdateFileTimestamps}
                              allTagsFlat={allTagsFlatState}
                              tagIdToTagMap={tagIdToTagMapState}
                              includeThumbnail={true}
                              showFileDetails={true}
                              showThumbnailGallery={true}
                              allHierarchicalTags={allHierarchicalTagsState}
                              onUpdateTags={handleUpdateFileTags}
                              areTagsLoading={isLoadingTags}
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              formSubmissions={(formData?.formSubmissions ?? []) as any}
                              areFormSubmissionsLoading={formData?.areFormSubmissionsLoading ?? false}
                              onFormUpdated={formData?.handleFormSubmissionsUpdated ?? (() => Promise.resolve())}
                              selectedWorklist={null}
                              allWorklists={null}
                            />
                          </div>
                        )}
                      </div>
                    </FileViewerProvider>
                  </ApiWebSocketProvider>
                )}
              </>
            )}
          </StyledTabsContentDiv>

          {(sessionLocation?.id ?? patient?.location_Id) && isITransplantVisible && (
            <ITransplantModal
              mergeDonorData={iTransplantData}
              onUpdateDonor={handleITransplantUpdate}
              onStatusChange={handleITransplantStatusChange}
              key={id}
              donorId={id}
              toggleITransplantDialog={toggleITransplantDialog}
              visible={isITransplantVisible}
              locationId={sessionLocation?.id ?? patient?.location_Id ?? 0}
              isOpo={true}
            />
          )}
          {isMergeDonorVisible && (
            <MergePatientModal
              isOPO={isOpo}
              showDonorGrid={true}
              key={isMergeDonorVisible.toString()}
              srcDonorId={0}
              destDonorId={id}
              toggleDialog={toggleMergeDonorDialog}
              targetPatient={patient}
            />
          )}
          {patient != null && isUploadFileModalVisible && <UploadFileModal patient={patient} onClose={handleUploadFileModalClose} />}
          {selectedExamId > 0 && linkId && <QRScanModal show={isQrModalVisible} onClose={handleQrModalClose} examId={selectedExamId} linkId={linkId} />}
          {isDonorViewFilesVisible && <UploadWindow onClose={handleViewAttachments} examId={selectedExamId} onChange={examsQuery.refetch}></UploadWindow>}
          <PatientFileViewModal
            toggleDialog={handleViewDicomImages}
            visible={isDicomImagesVisible}
            examStatus={selectedExamStatus}
            examId={selectedExamId}
            isOPO={isOpo}
            feature={PatientFileModalFeature.IMAGES}
            showEditBtn
            showDeleteBtn
            isQrModalVisible={false}
            linkId=""
          />
          {isDonorViewFinalReportVisible && <ViewFileWindow title="Final Report" file={finalReport} onClose={handleFinalReportClosed} />}
          {patient && (
            <ShareModal show={isShareModalVisible} patientId={patient.id} examId={shareExamId} onHide={setShareModalHidden} onPatientChange={setPatient} />
          )}
          {showEditModal && readRequestMode && (
            <PatientEditModal
              patientId={id}
              onClose={closeEditWindow}
              onPatientSaved={() => {
                closeEditWindow();
                // Retry the read request after successful edit
                if (selectedExamId > 0) {
                  navigate(`/exam/${selectedExamId}/read-request`);
                }
              }}
            />
          )}
        </StyledPatientContainer>
      </Page>
    </>
  );
};

PatientEdit.displayName = 'PatientEdit';

const mediaQueryMobileWidth = '600px';

const StyledPatientContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};

  .accordion-item {
    background-color: transparent;
  }

  img {
    border-radius: 4px;
  }

  .file-viewer-page {
    height: 100%;
    overflow: hidden !important;
    width: 100% !important;
    max-height: 100vh !important; //todo: improve auto calculation
    min-height: calc(100vh - 130px);
  }
`;

const StyledIcon = styled(SvgIcon)`
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledPatientExamGrid = styled(PatientEditExamGrid)`
  flex-grow: 1;
`;

const StyledActionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.5rem;
  background-color: transparent;
  padding: 1rem 1rem 0 1rem;
  flex-shrink: 0;

  .k-button {
    padding-top: 0;
    padding-bottom: 0;

    /* Enhanced mobile styling for better touch targets */
    @media (max-width: ${mediaQueryMobileWidth}) {
      min-height: 44px; /* Minimum touch target size for accessibility */
      min-width: 44px;
      padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing30};
      font-size: ${({ theme }) => theme.fontSizes.body};

      /* Improve visual feedback on mobile */
      &:active {
        transform: scale(0.98);
        transition: transform 0.1s ease;
      }
    }
  }

  /* Mobile-specific container styling */
  @media (max-width: ${mediaQueryMobileWidth}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.space.spacing20}; /* Larger gaps for easier touch */
    padding: ${({ theme }) => theme.space.spacing20};

    /* Ensure buttons stretch to full width on mobile for easier touch */
    .k-button {
      width: 100%;
      justify-content: flex-start; /* Left-align text for better readability */
      min-height: 44px;

      /* Add consistent spacing for icon + text layout */
      .k-icon,
      .fa {
        margin-right: ${({ theme }) => theme.space.spacing20};
      }
    }
  }
`;

const StyledPatientEditHeader = styled(MemoizedPatientEditHeader)`
  flex: 0 0 auto;
`;

const StyledTabsPanel = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: transparent;

  /* Mobile enhancements for tabs panel */
  @media (max-width: ${mediaQueryMobileWidth}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.space.spacing10};
    padding: 0 ${({ theme }) => theme.space.spacing20};

    /* Stack main tabs and toggle buttons vertically on mobile */
    > * {
      width: 100%;
    }
  }
`;

const StyledMainTabsContainer = styled(TabsContainer)`
  margin-right: 20px;

  /* Mobile enhancements for main tab buttons */
  @media (max-width: ${mediaQueryMobileWidth}) {
    margin-right: 0;
    width: 100%;
    overflow-x: auto; /* Allow horizontal scrolling if tabs don't fit */

    /* Hide scrollbar for cleaner appearance */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }

    /* Enhance tab buttons for mobile */
    .k-button {
      min-height: 44px; /* Accessibility compliance */
      padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing30};
      white-space: nowrap; /* Prevent text wrapping */

      /* Improve visual feedback */
      &:active {
        transform: scale(0.98);
        transition: transform 0.1s ease;
      }
    }
  }
`;

const StyledTabsContentDiv = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StyledToggleActiveExamsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    margin-left: auto;
  }

  /* Mobile enhancements for toggle buttons */
  @media (max-width: ${mediaQueryMobileWidth}) {
    width: 100%;
    justify-content: center;
    margin: ${({ theme }) => theme.space.spacing10} 0;

    /* Enhance tab buttons for mobile touch targets */
    .k-button {
      min-height: 44px; /* Accessibility compliance */
      padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing30};
      flex: 1; /* Equal width buttons */

      /* Improve visual feedback */
      &:active {
        transform: scale(0.98);
        transition: transform 0.1s ease;
      }
    }
  }
`;
